import { useCallback, useState } from 'react';

import { useConstantCallback } from '@motorway/mw-highway-code/hooks';

import { SNOWPLOW_EVENTS } from 'Services/analytics/analytics.const';
import { motorwayPayPageEvents } from 'Services/analytics/events/paymentEvents/motorwayPayPageEvents';
import { validatePhone } from 'Services/api/miscellaneous';
import useUser from 'Utilities/hooks/useUser';

import type { DiscoverMWPayModalState } from '../DiscoverMWPay.types';

import type {
	RequestCallbackPayload,
	useRequestCallbackParams,
	useRequestCallbackReturn,
} from './ModalRequestCallback.types';

export const useRequestCallback = ({
	callback,
	defaultState = 'closed',
	withValidatePhone = true,
}: useRequestCallbackParams): useRequestCallbackReturn => {
	const [callbackModalState, setCallbackModalState] = useState<DiscoverMWPayModalState>(defaultState);
	const user = useUser();

	const staticCallback = useConstantCallback(callback);

	const closeModal = useCallback(() => {
		motorwayPayPageEvents({
			name: 'Request a call back modal closed',
			schemaName: SNOWPLOW_EVENTS.CLICK.SCHEMA,
			version: SNOWPLOW_EVENTS.CLICK.VERSION,
		});
		setCallbackModalState('closed');
	}, []);

	const openModal = useCallback(() => setCallbackModalState('open'), []);

	const makeCall = async (payload: RequestCallbackPayload) => {
		const { status } = await staticCallback(payload);
		setCallbackModalState(status === 'success' ? 'success' : 'error');
	};

	const onSubmit = useCallback(
		async (payload: RequestCallbackPayload) => {
			motorwayPayPageEvents({
				name: 'Request a call back form submitted',
				schemaName: SNOWPLOW_EVENTS.CLICK.SCHEMA,
				version: SNOWPLOW_EVENTS.CLICK.VERSION,
			});
			setCallbackModalState('loading');

			try {
				if (withValidatePhone) {
					const { valid } = await validatePhone(payload.phone);
					if (valid === false) {
						setCallbackModalState('invalidPhone');
					} else {
						await makeCall(payload);
					}
				} else {
					await makeCall(payload);
				}
			} catch (error) {
				setCallbackModalState('error');
			}
		},
		[makeCall, withValidatePhone],
	);

	return {
		closeModal,
		onSubmit,
		openModal,
		state: callbackModalState,
		user: {
			name: user.name,
			phone: user.phone,
		},
	};
};
